/**
 * A small, fixed list of accessibility-flavoured starter prompts shown as chips
 * on the venue assistant's empty state. Each entry references an i18n key so the
 * UI layer resolves the display text (and the raw English fallback) itself.
 */
export type StarterQuestion = {
  id: string;
  i18nKey: string;
};

export const VENUE_ASSISTANT_STARTER_QUESTIONS: StarterQuestion[] = [
  { id: "wheelchair", i18nKey: "askAiStarterWheelchair" },
  { id: "openingHours", i18nKey: "askAiStarterOpeningHours" },
  { id: "stepsEntrance", i18nKey: "askAiStarterSteps" },
  { id: "accessibleBathroom", i18nKey: "askAiStarterBathroom" },
  { id: "openNow", i18nKey: "askAiStarterOpenNow" },
];

/** Contribution-oriented prompts for venues that have no accessibility data yet.
 *  Asking the accessibility questions above would only return "I don't have that",
 *  so we steer the visitor towards questions the assistant can actually act on. */
const VENUE_ASSISTANT_EMPTY_STARTER_QUESTIONS: StarterQuestion[] = [
  { id: "addInfo", i18nKey: "askAiStarterAddInfo" },
  { id: "getNotified", i18nKey: "askAiStarterGetNotified" },
  { id: "address", i18nKey: "askAiStarterAddress" },
];

export type VenueAssistantCapabilities = {
  /** Whether the venue has any accessibility features/reviews mapped. */
  hasAccessibilityData?: boolean;
  /** Whether the venue has opening hours listed. */
  hasOpeningHours?: boolean;
};

/**
 * Pick the starter chips to show for a venue. Defaults to the full accessibility
 * set (current behaviour) unless we positively know the venue has no accessibility
 * data — in that case we show hours (when available) plus contribution prompts, so
 * empty venues don't lead visitors into a string of "I don't have that" replies.
 */
export function getVenueAssistantStarterQuestions(
  capabilities?: VenueAssistantCapabilities,
): StarterQuestion[] {
  const hasAccessibilityData = capabilities?.hasAccessibilityData ?? true;
  if (hasAccessibilityData) {
    return VENUE_ASSISTANT_STARTER_QUESTIONS;
  }

  const questions: StarterQuestion[] = [];
  if (capabilities?.hasOpeningHours) {
    questions.push({ id: "openNow", i18nKey: "askAiStarterOpenNow" });
  }
  questions.push(...VENUE_ASSISTANT_EMPTY_STARTER_QUESTIONS);
  return questions;
}
